import { useEffect, useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist";
import pdfWorker from "pdfjs-dist/build/pdf.worker.min?url";
import {
  Box,
  Button,
  Dialog,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useMediaQuery,
  useTheme
} from "@mui/material";
import Iconify from "../../../minimals/components/iconify";
import { OrderDocumentDetail } from "../types";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker;

/**
 * In-page document preview.
 *
 * Renders with pdf.js rather than an <iframe src="/opendoc?id=..."> because that
 * endpoint returns a raw application/pdf: desktop browsers show their built-in
 * viewer, but iOS Safari renders only the first page (no scrolling) and Android
 * Chrome typically shows nothing at all. Canvas rendering behaves the same on
 * every browser, and pdf.js is already a dependency (see DocumentAiReview).
 */
export function DocumentPreviewDialog({
  document,
  selected,
  onToggleDocumentSelection,
  onClose
}: {
  document: OrderDocumentDetail | null;
  selected: boolean;
  onToggleDocumentSelection: (documentId: number, selected: boolean) => void;
  onClose: () => void;
}) {
  const [pdf, setPdf] = useState<any>(null);
  const [pageNumbers, setPageNumbers] = useState<number[]>([]);
  const [error, setError] = useState("");
  const [pageWidth, setPageWidth] = useState(720);
  const viewerRef = useRef<HTMLDivElement | null>(null);
  const documentId = document?.id;

  useEffect(() => {
    if (!documentId) {
      return;
    }

    let cancelled = false;
    let loadingTask: pdfjsLib.PDFDocumentLoadingTask | null = null;
    setPdf(null);
    setPageNumbers([]);
    setError("");

    const loadPdf = async () => {
      try {
        const response = await fetch(`/opendoc?id=${documentId}`);
        if (!response.ok) {
          throw new Error("Could not load PDF.");
        }

        const data = await response.arrayBuffer();
        if (cancelled) return;

        loadingTask = pdfjsLib.getDocument({
          data,
          cMapPacked: true,
          cMapUrl: "/pdfjs/cmaps/",
          iccUrl: "/pdfjs/iccs/",
          standardFontDataUrl: "/pdfjs/standard_fonts/",
          useWasm: true,
          wasmUrl: "/pdfjs/wasm/"
        });
        const loadedPdf = await loadingTask.promise;

        if (cancelled) {
          return;
        }

        setPdf(loadedPdf);
        setPageNumbers(Array.from({ length: loadedPdf.numPages }, (_, index) => index + 1));
      } catch {
        if (!cancelled) {
          setError("This document could not be previewed.");
        }
      }
    };

    loadPdf();

    return () => {
      cancelled = true;
      void loadingTask?.destroy();
    };
  }, [documentId]);

  // Track the dialog body width so pages fill it without overflowing.
  useEffect(() => {
    if (!documentId) {
      return;
    }

    const updateWidth = () => {
      const containerWidth = viewerRef.current?.clientWidth;
      if (containerWidth) {
        setPageWidth(Math.max(280, containerWidth - 16));
      }
    };

    const frame = window.requestAnimationFrame(updateWidth);
    window.addEventListener("resize", updateWidth);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", updateWidth);
    };
  }, [documentId, pdf]);

  const title = document?.documentDescription || (document ? `Document ${document.id}` : "");

  // Edge-to-edge on mobile. Note this must be the `fullScreen` prop rather than sx:
  // the theme's MuiDialog override rounds the paper via `&.MuiPaper-rounded` (two
  // classes) and insets it with a max-width:600px margin, both of which outrank sx.
  // It also zeroes the radius for `&.MuiDialog-paperFullScreen`, so the prop wins.
  const fullScreen = useMediaQuery(useTheme().breakpoints.down("md"));

  return (
    <Dialog
      open={Boolean(document)}
      onClose={onClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: fullScreen
          ? undefined
          : {
              m: 2,
              width: "auto",
              maxWidth: 900,
              height: "90vh",
              maxHeight: "90vh",
              borderRadius: 1.5
            }
      }}
    >
      <Stack sx={{ height: "100%", minHeight: 0 }}>
        {/* Title plus dismiss. The document actions stay in the bottom bar within
            thumb reach; close sits top-right where users expect to find it. */}
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{
            px: 1.5,
            py: 1,
            borderBottom: "1px solid",
            borderColor: "divider",
            flexShrink: 0
          }}
        >
          <Typography
            sx={{
              flex: 1,
              minWidth: 0,
              color: "#000e48",
              fontSize: 14,
              fontWeight: 600,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap"
            }}
          >
            {title}
          </Typography>
          <IconButton
            aria-label="Close"
            onClick={onClose}
            sx={{ width: 36, height: 36, p: 0, flexShrink: 0, color: "#000e48" }}
          >
            <Iconify icon="eva:close-fill" width={22} />
          </IconButton>
        </Stack>

        <Box
          ref={viewerRef}
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "auto",
            backgroundColor: (theme) => alpha(theme.palette.grey[500], 0.16),
            px: 1,
            py: 1
          }}
        >
          {error && (
            <Stack spacing={1} alignItems="center" sx={{ py: 6, color: "text.secondary" }}>
              <Iconify icon="eva:alert-triangle-outline" width={28} />
              <Typography variant="body2">{error}</Typography>
              <Typography variant="caption">Use “Open in new tab” to view it directly.</Typography>
            </Stack>
          )}

          {!error && !pdf && (
            <Typography variant="body2" sx={{ py: 6, textAlign: "center", color: "text.secondary" }}>
              Loading document...
            </Typography>
          )}

          {pdf && (
            <Stack spacing={1} alignItems="center">
              {pageNumbers.map((pageNumber) => (
                <PdfPreviewPage
                  key={`${documentId}-${pageNumber}`}
                  pdf={pdf}
                  pageNumber={pageNumber}
                  targetWidth={pageWidth}
                />
              ))}
            </Stack>
          )}
        </Box>

        {/* Actions pinned to the bottom so they fall under the thumb rather than at
            the top of a full-height sheet. Extra padding clears the iOS home bar. */}
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="space-around"
          sx={{
            px: 1.5,
            pt: 1,
            pb: { xs: "calc(8px + env(safe-area-inset-bottom))", sm: 1 },
            borderTop: "1px solid",
            borderColor: "divider",
            backgroundColor: "background.paper",
            flexShrink: 0
          }}
        >
          {documentId && (
            <>
              {/* Mirrors the row checkbox, then dismisses — selecting from a preview
                  is a "yes, include this one" gesture, so returning to the list is
                  the expected next step. */}
              <Button
                size="small"
                variant={selected ? "contained" : "outlined"}
                startIcon={
                  <Iconify
                    icon={selected ? "eva:checkmark-square-2-fill" : "eva:square-outline"}
                    width={20}
                  />
                }
                onClick={() => {
                  onToggleDocumentSelection(documentId, !selected);
                  onClose();
                }}
                sx={{ height: 40, flexShrink: 0 }}
              >
                {selected ? "Deselect" : "Select"}
              </Button>
              <Tooltip title="Open in new tab">
                <IconButton
                  component="a"
                  href={`/opendoc?id=${documentId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ width: 44, height: 44, color: "#000e48" }}
                >
                  <Iconify icon="eva:external-link-outline" width={22} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Download">
                <IconButton
                  component="a"
                  href={`/opendoc/download?id=${documentId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ width: 44, height: 44, color: "#000e48" }}
                >
                  <Iconify icon="eva:download-fill" width={22} />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Stack>
      </Stack>
    </Dialog>
  );
}

function PdfPreviewPage({
  pdf,
  pageNumber,
  targetWidth
}: {
  pdf: any;
  pageNumber: number;
  targetWidth: number;
}) {
  const shellRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  // Render the first couple of pages immediately; the rest wait until they are
  // near the viewport so a 200-page document does not rasterize up front.
  const [isVisible, setIsVisible] = useState(pageNumber <= 2);

  useEffect(() => {
    const element = shellRef.current;
    if (!element || isVisible) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setIsVisible(true);
        }
      },
      { rootMargin: "1200px 0px" }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [isVisible]);

  useEffect(() => {
    let cancelled = false;
    let renderTask: { cancel: () => void } | null = null;

    const render = async () => {
      const page = await pdf.getPage(pageNumber);
      const baseViewport = page.getViewport({ scale: 1 });
      const cssScale = targetWidth / baseViewport.width;
      const cssViewport = page.getViewport({ scale: cssScale });
      if (cancelled) {
        return;
      }

      setSize({ width: cssViewport.width, height: cssViewport.height });

      if (!isVisible) {
        return;
      }

      const pixelRatio = window.devicePixelRatio || 1;
      const viewport = page.getViewport({ scale: cssScale * pixelRatio });
      const canvas = canvasRef.current;
      const context = canvas?.getContext("2d");
      if (!canvas || !context || cancelled) {
        return;
      }

      canvas.width = viewport.width;
      canvas.height = viewport.height;
      canvas.style.width = `${cssViewport.width}px`;
      canvas.style.height = `${cssViewport.height}px`;

      const currentTask = page.render({ canvasContext: context, viewport });
      renderTask = currentTask;
      try {
        await currentTask.promise;
      } catch {
        // Cancelled renders throw; nothing to surface.
      }
    };

    render();

    return () => {
      cancelled = true;
      renderTask?.cancel();
    };
  }, [pdf, pageNumber, targetWidth, isVisible]);

  return (
    <Box
      ref={shellRef}
      sx={{
        width: size.width || targetWidth,
        height: size.height || targetWidth * 1.29,
        backgroundColor: "background.paper",
        boxShadow: "0 2px 8px rgba(22, 28, 36, 0.16)",
        flexShrink: 0
      }}
    >
      {isVisible && <canvas ref={canvasRef} />}
    </Box>
  );
}
